import React, { useState, useEffect } from 'react';
import {
    Box,
    Container,
    Typography,
    Button,
    Alert,
    Stepper,
    Step,
    StepLabel,
    Card,
    CardContent,
    Divider,
    IconButton,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { CardRegisterButton } from '../../../components/Payment/CardRegisterButton';
import { useVendorStore } from '../../../store/vendorStore';
import { useLoginFormDialogStore } from '../../../store/loginFormDialogStore';
import { post } from '../../../helpers/api.helper';
import { getFittingProducts } from '../../../utils/subscriptionApi';
import config from '../../../config/config';

interface SubscriptionCheckoutPageProps {
    planId?: string;
    planTitle?: string;
    planPrice?: number | string;
    planPeriod?: string;
}

export function SubscriptionCheckoutPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const { vendorId } = useVendorStore();
    const { setIsOpen: openLoginDialog } = useLoginFormDialogStore((state) => state.action);

    // URL 파라미터 또는 location state에서 플랜 정보 가져오기
    const planData = (location.state as SubscriptionCheckoutPageProps) || {};
    const { planId = '', planTitle = '', planPrice = 0, planPeriod = '' } = planData;

    const [activeStep, setActiveStep] = useState(0);
    const [cardRegistered, setCardRegistered] = useState(false);
    const [subscriptionComplete, setSubscriptionComplete] = useState(false);
    const [billingData, setBillingData] = useState<any>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [fittingSubscriptionPlanId, setFittingSubscriptionPlanId] = useState<string>('');

    // 로그인 체크
    useEffect(() => {
        if (!vendorId) {
            openLoginDialog(true);
            navigate('/subscription');
        }
    }, [vendorId, openLoginDialog, navigate]);

    // fittingSubscriptionPlanId 가져오기
    useEffect(() => {
        const fetchFittingSubscriptionPlanId = async () => {
            if (planId) {
                try {
                    const response = await getFittingProducts();
                    const fittingProduct = response.data.find((item) => item.id === planId);
                    if (fittingProduct && fittingProduct.fittingSubscriptionPlans.length > 0) {
                        // 첫 번째 fittingSubscriptionPlan의 id 사용
                        setFittingSubscriptionPlanId(fittingProduct.fittingSubscriptionPlans[0].id);
                    }
                } catch (error) {
                    console.error('Failed to fetch fitting subscription plan id:', error);
                }
            }
        };

        fetchFittingSubscriptionPlanId();
    }, [planId]);

    // 플랜 정보가 없으면 구독 페이지로 리다이렉트
    useEffect(() => {
        if (!planId || !planTitle) {
            navigate('/subscription');
        }
    }, [planId, planTitle, navigate]);

    const steps = ['카드 등록', '구독 완료'];

    const handleCardRegistrationSuccess = (data: any) => {
        setBillingData(data);
        setCardRegistered(true);
        setActiveStep(1);

        if (data.BID) {
            handleCreateSubscription(data);
        } else {
            console.error('BID가 없어서 구독을 생성할 수 없습니다.');
        }
    };

    const handleCardRegistrationError = (error: Error) => {
        console.error('Card registration failed:', error);
        setError('카드 등록에 실패했습니다. 다시 시도해주세요.');
    };

    const handleCreateSubscription = async (cardData: any) => {
        try {
            setLoading(true);
            setError(null);

            // fittingSubscriptionPlanId가 없으면 에러
            if (!fittingSubscriptionPlanId) {
                throw new Error('구독 플랜 정보를 찾을 수 없습니다.');
            }

            const response = await post(`${config.api.matching_api}/billing/approve`, {
                subscriptionPlanId: fittingSubscriptionPlanId,
                userId: vendorId,
                bid: cardData.BID,
                amount: Number(planPrice),
                goodsName: planTitle,
            });

            const result = response.data;
            if (result && result.ResultCode === '3001') {
                setSubscriptionComplete(true);
            } else {
                const errorMsg = result?.ResultMsg || '구독 결제 처리에 실패했습니다.';
                throw new Error(errorMsg);
            }
        } catch (error) {
            console.error('Subscription creation failed:', error);
            setError(error instanceof Error ? error.message : '구독 생성에 실패했습니다.');
            setActiveStep(0);
        } finally {
            setLoading(false);
        }
    };

    const handleGoBack = () => {
        navigate('/subscription');
    };

    const handleComplete = () => {
        navigate('/subscription');
    };

    const renderPrice = () => {
        if (typeof planPrice === 'number') {
            return `${planPrice.toLocaleString()}원${planPeriod}`;
        }
        return `${planPrice}${planPeriod}`;
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                backgroundColor: '#ffffff',
                color: '#000000',
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <Container maxWidth="sm" sx={{ flex: 1, pb: 4 }}>
                <Typography
                    variant="h3"
                    sx={{
                        fontWeight: 700,
                        fontSize: { xs: '1rem', md: '2rem' },
                        textAlign: 'left',
                        maxWidth: '800px',
                        margin: '0 auto',
                        px: 2,
                        py: 4,
                    }}
                >
                    지금 바로 스트리밍을 시작하세요
                </Typography>
                {/* Plan Information Card */}
                <Card
                    sx={{
                        backgroundColor: '#f5f5f5', // 연한 회색
                        mb: 4,
                        borderRadius: '10px',
                        boxShadow: 'none',
                    }}
                >
                    <CardContent sx={{ p: 3 }}>
                        <Typography variant="h4" sx={{ mb: 2, fontWeight: 600 }}>
                            {planTitle} 플랜
                        </Typography>
                        <Typography variant="h4" sx={{ mb: 3, fontWeight: 700 }}>
                            {renderPrice()}(부가세 포함)
                        </Typography>

                        <Divider sx={{ my: 2, borderColor: '#333' }} />

                        <Typography variant="body2" sx={{ opacity: 0.7, fontSize: '0.675rem' }}>
                            구매 후 14일 이내 취소 가능하며, 결제 주기 종료 시 취소 처리됩니다. 즉시 취소하려면
                            고객센터로 문의해주세요.
                        </Typography>
                    </CardContent>
                </Card>

                {/* Payment Method Section */}
                <Box sx={{ mb: 4 }}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                        신용카드
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3, opacity: 0.7, fontSize: '0.675rem' }}>
                        구독 결제를 완료하려면 '동의 및 구독 결제하기'를 클릭하여 완료하세요.
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3, opacity: 0.7, fontSize: '0.675rem' }}>
                        '동의 및 구독 결제하기'를 클릭하면, 구독 결제를 완료함과 동시에 멤버십을 즉시 시작하게 되고{' '}
                        {planPeriod} ₩{planPrice}(부가세 포함, 변동 가능)의 구독료가 회원님의 저장된 결제 수단에 결제
                        주기마다 반복적으로 정기 청구됩니다. 멤버십은 계정 설정을 통하거나 고객센터에 문의해 언제든지
                        취소할 수 있으며, 구매 후 14일 이내 취소 가능하며, 결제 기간 종료 시 취소 처리됩니다. 즉시
                        취소를 비롯해 취소 및 환불에 대해 보다 자세히 알고 싶다면, 고객센터로 연락주시거나, 취소 및 환불
                        정책을 확인하세요. 멤버십 구독 시 Shop3 이용약관이 적용되는 것에 동의하게 됩니다. *연간 구독료의
                        경우 월간 멤버십 12개월 구독료 대비 할인된 가격입니다.
                    </Typography>
                </Box>

                {/* Error Display */}
                {error && (
                    <Alert severity="error" sx={{ mb: 3, backgroundColor: '#2d1b1b', border: '1px solid #d32f2f' }}>
                        {error}
                    </Alert>
                )}

                {/* Content Based on Step */}
                {activeStep === 0 && !cardRegistered && (
                    <Box>
                        <CardRegisterButton
                            onSuccess={handleCardRegistrationSuccess}
                            onError={handleCardRegistrationError}
                            variant="contained"
                            size="large"
                            fullWidth
                            amt={typeof planPrice === 'number' ? planPrice : 1000}
                            planId={planId}
                        />
                    </Box>
                )}

                {activeStep === 1 && !subscriptionComplete && (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                        <CheckCircleIcon sx={{ fontSize: 60, color: '#10B981', mb: 2 }} />
                        <Typography variant="h5" sx={{ mb: 2 }}>
                            카드 등록 완료
                        </Typography>
                    </Box>
                )}

                {subscriptionComplete && (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                        <CheckCircleIcon sx={{ fontSize: 60, color: '#10B981', mb: 2 }} />
                        <Typography variant="h5" sx={{ mb: 2, color: '#10B981' }}>
                            구독이 완료되었습니다.
                        </Typography>

                        <Button
                            variant="contained"
                            size="large"
                            onClick={handleComplete}
                            sx={{
                                backgroundColor: '#ffffff',
                                color: '#000000',
                                '&:hover': { backgroundColor: '#f5f5f5' },
                                px: 4,
                                py: 1.5,
                                fontWeight: 600,
                            }}
                        >
                            구독 페이지로 돌아가기
                        </Button>
                    </Box>
                )}
            </Container>
        </Box>
    );
}

export default SubscriptionCheckoutPage;
